/**
 * Per-wallet compatibility profiles (#687). Source of truth for the table in docs/research/WALLETS.md — keep the
 * two in sync. Used by:
 *   - the wallet picker (order, support badge, unsupported warning, install links),
 *   - hl/exchange.ts signL1 (switch-first vs sign-first chain handling),
 *   - discovery.ts (naming a legacy window.ethereum provider from its is* flags).
 *
 * Hyperliquid L1 actions (order/cancel) are EIP-712 with domain chainId 1337. A wallet can sign them if it either
 * (a) lets us add + switch to a custom chain 1337, or (b) signs typed data whose domain chainId differs from the
 * active chain (only Frame, per its source). Wallets that do neither (Phantom's fixed-chain EVM provider) trade
 * through agent-wallet 1-click trading (#688), which needs only one approveAgent signed on the wallet's own chain.
 */
import type { EIP1193Provider } from './types';
import { t } from '../i18n/i18n';

export type WalletSupport = 'supported' | 'caveat' | 'unsupported';

/**
 * - 'switch-first': switch/add chain 1337 before signing (MetaMask rule: domain chainId must equal active chain).
 * - 'sign-first':   sign on the current chain first (wallet does not enforce the chainId match), and only switch
 *                   to 1337 if the wallet refuses. Saves a network prompt per order.
 */
export type SignStrategy = 'switch-first' | 'sign-first';

export interface WalletProfile {
  /** Stable key (also used in tests). */
  readonly id: string;
  readonly name: string;
  /** EIP-6963 rdns values this profile covers (exact match, or prefix + "."). */
  readonly rdns: readonly string[];
  /** window.ethereum flag that identifies the wallet when it is only injected (no EIP-6963). */
  readonly flag?: string;
  readonly support: WalletSupport;
  readonly strategy: SignStrategy;
  /** One-line note shown in the picker for 'caveat' / 'unsupported' (and in the ticket for 'unsupported'). */
  readonly note?: string;
  /**
   * Extra hint shown when the user cancels / the wallet rejects adding the "Hyperliquid EIP712 Signing" network
   * (e.g. Rabby refuses to save a network whose RPC does not answer).
   */
  readonly addChainHint?: string;
  readonly installUrl?: string;
  /** Offered as an install link when no wallet is detected. */
  readonly suggest?: boolean;
  /** Picker order (lower first). Unknown wallets get 50. */
  readonly rank: number;
}

// Notes, hints and the generic name are getters so they follow the active language (#799).

export const WALLET_PROFILES: readonly WalletProfile[] = [
  {
    id: 'metamask',
    name: 'MetaMask',
    rdns: ['io.metamask'],
    flag: 'isMetaMask',
    support: 'supported',
    strategy: 'switch-first',
    installUrl: 'https://metamask.io/download/',
    suggest: true,
    rank: 0,
  },
  {
    id: 'rabby',
    name: 'Rabby',
    rdns: ['io.rabby'],
    flag: 'isRabby',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.rabby');
    },
    get addChainHint() {
      return t('wallet.note.rabby_add_chain');
    },
    installUrl: 'https://rabby.io/',
    suggest: true,
    rank: 1,
  },
  {
    id: 'frame',
    name: 'Frame',
    rdns: ['sh.frame'],
    flag: 'isFrame',
    support: 'supported',
    strategy: 'sign-first',
    installUrl: 'https://frame.sh/',
    suggest: true,
    rank: 2,
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    rdns: ['com.coinbase.wallet'],
    flag: 'isCoinbaseWallet',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.add_network', { untested: t('wallet.note.untested') });
    },
    installUrl: 'https://www.coinbase.com/wallet/downloads',
    suggest: true,
    rank: 3,
  },
  {
    id: 'okx',
    name: 'OKX Wallet',
    rdns: ['com.okex.wallet'],
    flag: 'isOkxWallet',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.add_network', { untested: t('wallet.note.untested') });
    },
    installUrl: 'https://www.okx.com/web3',
    rank: 4,
  },
  {
    id: 'brave',
    name: 'Brave Wallet',
    rdns: ['com.brave.wallet'],
    flag: 'isBraveWallet',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.add_network', { untested: t('wallet.note.untested') });
    },
    rank: 5,
  },
  {
    id: 'rainbow',
    name: 'Rainbow',
    rdns: ['me.rainbow'],
    flag: 'isRainbow',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.add_custom', { untested: t('wallet.note.untested') });
    },
    rank: 6,
  },
  {
    id: 'trust',
    name: 'Trust Wallet',
    rdns: ['com.trustwallet.app'],
    flag: 'isTrust',
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.add_custom', { untested: t('wallet.note.untested') });
    },
    rank: 7,
  },
  {
    id: 'phantom',
    name: 'Phantom',
    rdns: ['app.phantom'],
    flag: 'isPhantom',
    // #688: per-order signing needs chain 1337, which Phantom can't add; 1-click trading needs only one
    // approveAgent signed on Phantom's own network.
    support: 'caveat',
    strategy: 'switch-first',
    get note() {
      return t('wallet.note.phantom');
    },
    rank: 90,
  },
];

export const GENERIC_PROFILE: WalletProfile = {
  id: 'generic',
  get name() {
    return t('wallet.generic_name');
  },
  rdns: [],
  support: 'caveat',
  strategy: 'switch-first',
  get note() {
    return t('wallet.note.generic');
  },
  rank: 50,
};

function rdnsMatches(rdns: string, p: WalletProfile): boolean {
  return p.rdns.some((r) => rdns === r || rdns.startsWith(`${r}.`));
}

type Flags = Partial<Record<string, unknown>>;

/**
 * Wallets that also set isMetaMask (Rabby, Brave, OKX, Trust, Coinbase in some modes) are checked first, so a
 * legacy injected provider is named after what it really is.
 */
export function profileFromFlags(provider: EIP1193Provider | null | undefined): WalletProfile | null {
  if (!provider) return null;
  const f = provider as unknown as Flags;
  const has = (k: string) => f[k] === true;
  // Rabby, Brave, OKX, Trust (and Rabby's "MetaMask mode") also set isMetaMask: check MetaMask last.
  const order = ['phantom', 'rabby', 'brave', 'okx', 'trust', 'rainbow', 'frame', 'coinbase', 'metamask'];
  for (const id of order) {
    const p = WALLET_PROFILES.find((x) => x.id === id);
    if (!p?.flag) continue;
    if (has(p.flag)) return p;
    // Alternate spellings seen in the wild.
    if (id === 'okx' && (has('isOKExWallet') || has('isOKXWallet'))) return p;
    if (id === 'trust' && has('isTrustWallet')) return p;
  }
  return null;
}

/**
 * Profile for a discovered wallet. EIP-6963 wallets are identified by rdns only (their is* flags are not trusted:
 * many set isMetaMask). The legacy injected entry (rdns 'injected' or none) is identified by its flags.
 */
export function walletProfile(rdns?: string | null, provider?: EIP1193Provider | null): WalletProfile {
  if (rdns && rdns !== 'injected') return WALLET_PROFILES.find((x) => rdnsMatches(rdns, x)) ?? GENERIC_PROFILE;
  return profileFromFlags(provider) ?? GENERIC_PROFILE;
}

/** Wallets offered as install links when none is detected (supported first). */
export function installSuggestions(): WalletProfile[] {
  return WALLET_PROFILES.filter((p) => p.suggest && p.installUrl);
}

/** What the wallet will ask for when signing an L1 action (shown in the "Confirm in your wallet" dialog). */
export function chainPromptText(profile: WalletProfile): string {
  if (profile.strategy === 'sign-first') return t('wallet.chain_prompt.sign_first', { wallet: profile.name });
  return t('wallet.chain_prompt.switch');
}
